import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, FileWarning, Mail, FileDown, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;
const de = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join(".") : "—");
const fehler = (err, fallback) => err?.response?.data?.detail || fallback;

export default function MahnungenTab({ pwd }) {
  const [daten, setDaten] = useState({ posten: [], stufen: [], basiszins_prozent: 1.52 });
  const [historie, setHistorie] = useState([]);
  const [aktiv, setAktiv] = useState(null);     // offener Posten im Formular
  const [form, setForm] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [busy, setBusy] = useState(false);

  const kopf = useMemo(() => ({ headers: { "X-Admin-Password": pwd } }), [pwd]);

  const laden = useCallback(async () => {
    try {
      const [offen, hist] = await Promise.all([
        axios.get(`${API}/admin/mahnwesen/offen`, kopf),
        axios.get(`${API}/admin/mahnwesen`, kopf),
      ]);
      setDaten(offen.data);
      setHistorie(hist.data || []);
    } catch (err) { toast.error(fehler(err, "Offene Rechnungen konnten nicht geladen werden")); }
  }, [kopf]);

  useEffect(() => { laden(); }, [laden]);

  const stufeGebuehr = (stufe) =>
    (daten.stufen || []).find((s) => s.stufe === stufe)?.gebuehr ?? 0;

  const oeffnen = (p) => {
    setAktiv(p);
    setVorschau(null);
    setForm({
      quelle: p.quelle, ref_id: p.ref_id, stufe: p.naechste_stufe || 1,
      mahngebuehr_eur: stufeGebuehr(p.naechste_stufe || 1),
      basiszins_prozent: daten.basiszins_prozent,
      verzugspauschale: p.kundentyp !== "privat",
      frist_tage: 7, faellig_am: (p.faellig_am || "").slice(0, 10), zusatztext: "",
    });
  };

  const rechnen = useCallback(async (f) => {
    if (!f) return;
    try {
      const { data } = await axios.post(`${API}/admin/mahnwesen/vorschau`, f, kopf);
      setVorschau(data);
    } catch (err) { toast.error(fehler(err, "Vorschau nicht möglich")); }
  }, [kopf]);

  useEffect(() => { rechnen(form); }, [form, rechnen]);

  const setzen = (feld, wert) => setForm((f) => {
    const neu = { ...f, [feld]: wert };
    if (feld === "stufe") neu.mahngebuehr_eur = stufeGebuehr(wert);
    return neu;
  });

  const pdfOeffnen = async (id) => {
    try {
      const { data: blob } = await axios.get(`${API}/admin/mahnwesen/${id}/pdf`,
        { ...kopf, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(fehler(err, "PDF konnte nicht geöffnet werden")); }
  };

  const anlegen = async (senden) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/mahnwesen`, { ...form, senden }, kopf);
      toast.success(senden
        ? `${data.stufe_titel} an ${data.email} verschickt`
        : `${data.stufe_titel} ${data.nummer} erstellt`);
      setAktiv(null); setForm(null); setVorschau(null);
      await laden();
      if (!senden) await pdfOeffnen(data.id);
    } catch (err) {
      toast.error(fehler(err, "Mahnung konnte nicht erstellt werden"));
    } finally { setBusy(false); }
  };

  const ueberfaellig = (daten.posten || []).filter((p) => p.ueberfaellig);
  const laufend = (daten.posten || []).filter((p) => !p.ueberfaellig);

  const Zeile = ({ p }) => (
    <div className="border border-[#E3DECE] bg-white px-4 py-3" data-testid={`mahn-posten-${p.ref_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-serif text-lg text-[#2C2A26]">{p.betrieb || p.ansprechpartner}</p>
          <p className="text-xs text-[#757067]">
            {[p.nummer, `Rechnung ${de(p.datum)}`, `fällig ${de(p.faellig_am)}`,
              p.kundentyp === "privat" ? "Privatkunde" : "Betrieb"].filter(Boolean).join(" · ")}
          </p>
          {p.mahnstufe > 0 && (
            <p className="text-xs text-[#BA6D52] mt-1">
              Bereits gemahnt — Stufe {p.mahnstufe} am {de(p.letzte_mahnung_am)}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="text-base text-[#2C2A26]">{eur(p.brutto_eur)}</p>
          {p.ueberfaellig && (
            <p className="text-xs text-[#B3261E] inline-flex items-center gap-1 justify-end">
              <AlertTriangle size={12} /> {p.tage_ueberfaellig} Tage überfällig
            </p>
          )}
        </div>
        <button onClick={() => oeffnen(p)} data-testid={`mahn-start-${p.ref_id}`}
          className="bg-[#BA6D52] hover:bg-[#A55C43] text-white text-[10px] uppercase tracking-[0.18em] px-4 py-2.5 transition-colors">
          Mahnung schreiben
        </button>
      </div>

      {aktiv?.ref_id === p.ref_id && form && (
        <div className="mt-4 border-t border-[#E3DECE] pt-4 space-y-4" data-testid="mahn-formular">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-1">Stufe</span>
              <select value={form.stufe} onChange={(e) => setzen("stufe", Number(e.target.value))}
                data-testid="mahn-stufe"
                className="w-full border border-[#E3DECE] bg-[#FAFAF7] px-3 py-2 text-sm">
                {(daten.stufen || []).map((s) => (
                  <option key={s.stufe} value={s.stufe}>{s.titel}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-1">Mahngebühr €</span>
              <input type="number" step="0.5" min="0" value={form.mahngebuehr_eur}
                onChange={(e) => setzen("mahngebuehr_eur", Number(e.target.value))}
                data-testid="mahn-gebuehr"
                className="w-full border border-[#E3DECE] bg-[#FAFAF7] px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-1">Zahlungsfrist (Tage)</span>
              <input type="number" min="1" value={form.frist_tage}
                onChange={(e) => setzen("frist_tage", Number(e.target.value))}
                data-testid="mahn-frist"
                className="w-full border border-[#E3DECE] bg-[#FAFAF7] px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-1">Basiszinssatz %</span>
              <input type="number" step="0.01" value={form.basiszins_prozent}
                onChange={(e) => setzen("basiszins_prozent", Number(e.target.value))}
                data-testid="mahn-basiszins"
                className="w-full border border-[#E3DECE] bg-[#FAFAF7] px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-1">
                Fällig war am
              </span>
              <input type="date" value={form.faellig_am || (p.faellig_am || "").slice(0, 10)}
                onChange={(e) => setzen("faellig_am", e.target.value)}
                data-testid="mahn-faellig"
                className="w-full border border-[#E3DECE] bg-[#FAFAF7] px-3 py-2 text-sm" />
            </label>
          </div>

          {p.kundentyp !== "privat" && form.stufe >= 2 && (
            <label className="flex items-start gap-2 text-sm text-[#575249]">
              <input type="checkbox" checked={form.verzugspauschale} data-testid="mahn-pauschale"
                onChange={(e) => setzen("verzugspauschale", e.target.checked)} className="mt-1" />
              <span>40 € Verzugspauschale berechnen (§ 288 Abs. 5 BGB — nur gegenüber Betrieben zulässig)</span>
            </label>
          )}

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-1">
              Zusätzlicher Hinweis (optional)
            </span>
            <textarea rows={2} value={form.zusatztext} data-testid="mahn-zusatz"
              onChange={(e) => setzen("zusatztext", e.target.value)}
              placeholder="z. B. Hinweis auf mehrfach zugesagte, aber nicht erfolgte Zahlung"
              className="w-full border border-[#E3DECE] bg-[#FAFAF7] px-3 py-2 text-sm" />
          </label>

          {vorschau && (
            <div className="bg-[#FAF5F1] border border-[#E3DECE] px-4 py-3 text-sm" data-testid="mahn-vorschau">
              <div className="flex justify-between"><span className="text-[#757067]">Rechnungsbetrag</span>
                <span>{eur(vorschau.rechnungsbetrag_eur)}</span></div>
              {vorschau.bisherige_kosten_eur > 0 && (
                <div className="flex justify-between"><span className="text-[#757067]">Bisherige Mahnkosten</span>
                  <span>{eur(vorschau.bisherige_kosten_eur)}</span></div>
              )}
              {vorschau.mahngebuehr_eur > 0 && (
                <div className="flex justify-between"><span className="text-[#757067]">Mahngebühr</span>
                  <span>{eur(vorschau.mahngebuehr_eur)}</span></div>
              )}
              {vorschau.verzugspauschale_eur > 0 && (
                <div className="flex justify-between"><span className="text-[#757067]">Verzugspauschale</span>
                  <span>{eur(vorschau.verzugspauschale_eur)}</span></div>
              )}
              {vorschau.zinsen_eur > 0 && (
                <div className="flex justify-between">
                  <span className="text-[#757067]">
                    Verzugszinsen {String(vorschau.zinssatz_prozent).replace(".", ",")} % · {vorschau.verzugstage} Tage
                  </span>
                  <span>{eur(vorschau.zinsen_eur)}</span></div>
              )}
              <div className="flex justify-between border-t border-[#E3DECE] mt-2 pt-2 font-semibold">
                <span>Gesamtbetrag</span><span>{eur(vorschau.gesamt_eur)}</span>
              </div>
              <p className="text-xs text-[#757067] mt-2">Zahlbar bis {de(vorschau.frist_bis)}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button disabled={busy} onClick={() => anlegen(false)} data-testid="mahn-pdf"
              className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] text-[10px] uppercase tracking-[0.18em] px-4 py-2.5 inline-flex items-center gap-2 transition-colors disabled:opacity-50">
              <FileDown size={14} /> Nur PDF erstellen
            </button>
            <button disabled={busy || !p.email} onClick={() => anlegen(true)} data-testid="mahn-senden"
              className="bg-[#BA6D52] hover:bg-[#A55C43] text-white text-[10px] uppercase tracking-[0.18em] px-4 py-2.5 inline-flex items-center gap-2 transition-colors disabled:opacity-50">
              <Mail size={14} /> Per E-Mail senden
            </button>
            <button onClick={() => { setAktiv(null); setForm(null); setVorschau(null); }}
              data-testid="mahn-abbrechen"
              className="text-[10px] uppercase tracking-[0.18em] text-[#757067] px-3 py-2.5">
              Abbrechen
            </button>
          </div>
          {!p.email && (
            <p className="text-xs text-[#B3261E]">
              Für diesen Kunden ist keine E-Mail hinterlegt — bitte als PDF erstellen und per Post oder WhatsApp schicken.
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-10" data-testid="mahnungen-tab">
      <div>
        <h2 className="font-serif text-3xl text-[#2C2A26]">Mahnungen</h2>
        <p className="text-sm text-[#757067] mt-2 max-w-2xl">
          Alle gestellten, noch nicht bezahlten Rechnungen. Überfällige stehen oben. Jede Mahnung wird
          als PDF in Deinem Layout erzeugt — auf Wunsch direkt per E-Mail mit Anhang.
        </p>
      </div>

      <section className="space-y-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] inline-flex items-center gap-2">
          <FileWarning size={13} /> Überfällig ({ueberfaellig.length})
        </p>
        {ueberfaellig.length === 0
          ? <p className="text-sm text-[#757067] italic">Nichts überfällig — alles im grünen Bereich.</p>
          : ueberfaellig.map((p) => <Zeile key={`${p.quelle}-${p.ref_id}`} p={p} />)}
      </section>

      {laufend.length > 0 && (
        <section className="space-y-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">
            Noch in der Zahlungsfrist ({laufend.length})
          </p>
          {laufend.map((p) => <Zeile key={`${p.quelle}-${p.ref_id}`} p={p} />)}
        </section>
      )}

      {historie.length > 0 && (
        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] inline-flex items-center gap-2">
            <History size={13} /> Verschickte Mahnungen
          </p>
          {historie.map((m) => (
            <div key={m.id} data-testid={`mahn-historie-${m.id}`}
              className="border border-[#E3DECE] bg-[#FAFAF7] px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm text-[#2C2A26]">
                  {m.stufe_titel} · {m.betrieb} · Rechnung {m.rechnung_nummer}
                </p>
                <p className="text-xs text-[#757067]">
                  {[m.nummer, de(m.datum), `Frist ${de(m.frist_bis)}`,
                    m.gesendet_am ? "per E-Mail verschickt" : "nur als PDF"].join(" · ")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#2C2A26]">{eur(m.gesamt_eur)}</span>
                <button onClick={() => pdfOeffnen(m.id)} data-testid={`mahn-historie-pdf-${m.id}`}
                  className="text-[10px] uppercase tracking-[0.18em] text-[#BA6D52] inline-flex items-center gap-1">
                  <FileDown size={13} /> PDF
                </button>
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
